import { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';

import { CooldownParts, partsToSeconds, PRA_MIN_COOLDOWN_SECONDS, secondsToParts } from '../utils';

type Props = {
  cooldownSeconds: number;
  onChange: (seconds: number) => void;
};

const FIELDS: Array<{ key: keyof CooldownParts; label: string }> = [
  { key: 'days', label: 'Days' },
  { key: 'hours', label: 'Hours' },
  { key: 'minutes', label: 'Minutes' },
  { key: 'seconds', label: 'Seconds' },
];

// Cooldown editor as days/hours/minutes/SECONDS numeric fields summing to cooldownSeconds
// (the QA requirement surfaced in the UI, not just the API). The fields are free-form
// (25 hours is allowed and simply counts as 90000 seconds) — only the total matters.
export function PraCooldownEditor({ cooldownSeconds, onChange }: Props): JSX.Element {
  const [parts, setParts] = useState<CooldownParts>(() => secondsToParts(cooldownSeconds));

  // External resets (config load / post-save round-trip) re-derive the fields; while the
  // user types, the total already equals the prop, so their chosen split is preserved.
  useEffect(() => {
    setParts((prev) => (partsToSeconds(prev) === cooldownSeconds ? prev : secondsToParts(cooldownSeconds)));
  }, [cooldownSeconds]);

  const handleChange = (key: keyof CooldownParts, raw: string) => {
    const value = Math.max(0, Math.floor(Number(raw) || 0));
    const next = { ...parts, [key]: value };
    setParts(next);
    onChange(partsToSeconds(next));
  };

  const total = partsToSeconds(parts);
  const belowFloor = total < PRA_MIN_COOLDOWN_SECONDS;

  return (
    <Form.Group>
      <Form.Label className="font-weight-bold">Cooldown</Form.Label>
      <Row>
        {FIELDS.map(({ key, label }) => (
          <Col key={key} xs={6} md={2}>
            <Form.Label className="small text-muted mb-0">{label}</Form.Label>
            <Form.Control type="number" min={0} value={parts[key]} onChange={(e) => handleChange(key, e.target.value)} />
          </Col>
        ))}
        <Col className="d-flex align-items-end pb-1">
          <span className={belowFloor ? 'text-danger' : 'text-muted'}>= {total} seconds</span>
        </Col>
      </Row>
      {belowFloor && (
        // Mirrors the server-side error string (validation floor D10 — purely against fat-finger 0).
        <Form.Text className="text-danger">
          cooldownSeconds must be an integer &gt;= {PRA_MIN_COOLDOWN_SECONDS}.
        </Form.Text>
      )}
    </Form.Group>
  );
}
